import { useState, useEffect } from 'react';
import { Clock, Bell, Share2, Heart, Users, Star, ArrowRight, Calendar } from 'lucide-react';
import { addToCalender } from '../api/addToCalender';

const GloryHour = () => {
    const [nextSession, setNextSession] = useState(null);
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isReminderSet, setIsReminderSet] = useState(false);
    const eventAdd = {
        title: "Glory Hour",
        description: "GLORY HOUR WITH LATANGELA ROGERS.",
        location: "Online",
        start: [2025, 4, 20, 14, 30], // [YYYY, M, D, H, M]
        duration: { hours: 1 },
        status: "CONFIRMED",
    }

    useEffect(() => {
        // Calculate next session (Monday or Wednesday at 3 AM PST)
        const calculateNextSession = () => {
            const now = new Date();
            const pstDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
            let nextDate = new Date(pstDate);

            // Set to 3 AM
            nextDate.setHours(3, 0, 0, 0);

            // If it's past 3 AM today, move to next day
            if (pstDate.getHours() >= 3) {
                nextDate.setDate(nextDate.getDate() + 1);
            }

            // Find next Monday or Wednesday
            while (nextDate.getDay() !== 1 && nextDate.getDay() !== 3) {
                nextDate.setDate(nextDate.getDate() + 1);
            }

            setNextSession(nextDate);
        };

        calculateNextSession();
        const interval = setInterval(calculateNextSession, 1000 * 60); // Update every minute

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!nextSession) return;

        const updateCountdown = () => {
            const now = new Date();
            const diff = nextSession.getTime() - now.getTime();

            if (diff > 0) {
                setCountdown({
                    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((diff % (1000 * 60)) / 1000)
                });
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [nextSession]);

    const handleSetReminder = () => {
        addToCalender(eventAdd);
        // In a real implementation, this would integrate with a calendar API
        setIsReminderSet(!isReminderSet);
    };

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900">
            <div className="fixed top-0 left-0 w-full h-1 bg-black z-50"></div>

            <section className="relative min-h-screen flex items-center">
                <div className="absolute inset-0 bg-black">
                    <img
                        src="https://www.thetransformationcollective.org/wp-content/uploads/2025/11/WhatsApp-Image-2025-11-03-at-4.05.53-PM.jpeg"
                        alt="Glory Hour"
                        className="w-full h-full object-cover opacity-40 mix-blend-luminosity"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black"></div>
                </div>

                <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 max-w-[1800px] mx-auto">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end">
                        <div className="lg:col-span-7 space-y-8">
                            <div className="space-y-4">
                                <div className="bg-red-400 text-black px-6 py-2 inline-block font-bold text-sm tracking-widest">
                                    LIVE SPIRITUAL GATHERING
                                </div>
                                <h1 className="text-white font-black text-7xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tighter">
                                    GLORY<br/>HOUR
                                </h1>
                                <div className="flex items-center gap-4 text-white">
                                    <div className="w-12 h-[2px] bg-red-400"></div>
                                    <p className="text-xl md:text-2xl font-light tracking-wide">with LaTangela Rogers</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4 pt-8">
                                <button
                                    onClick={handleSetReminder}
                                    className={`px-8 py-4 font-bold tracking-wide transition-all ${
                                        isReminderSet
                                            ? 'bg-white text-black'
                                            : 'bg-red-400 text-black hover:bg-red-300'
                                    }`}
                                >
                                    {isReminderSet ? '✓ REMINDER SET' : 'SET REMINDER'}
                                </button>
                                <button className="px-8 py-4 border-2 border-white text-white font-bold tracking-wide hover:bg-white hover:text-black transition-all">
                                    SHARE
                                </button>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="bg-black/80 border-4 border-white p-8 md:p-12 backdrop-blur-sm">
                                <div className="text-red-400 text-xs font-bold tracking-widest mb-6">NEXT SESSION</div>
                                <div className="grid grid-cols-4 gap-4 mb-8">
                                    {[
                                        { label: 'DAYS', value: countdown.days },
                                        { label: 'HRS', value: countdown.hours },
                                        { label: 'MIN', value: countdown.minutes },
                                        { label: 'SEC', value: countdown.seconds }
                                    ].map((item, index) => (
                                        <div key={index} className="text-center">
                                            <div className="text-5xl md:text-6xl font-black text-white tabular-nums">{String(item.value).padStart(2, '0')}</div>
                                            <div className="text-xs text-red-400 font-bold tracking-wider mt-2">{item.label}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="border-t-2 border-red-400 pt-6">
                                    <div className="text-white font-bold text-lg">MONDAY · 7:00 PM PST</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-black"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-400 opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>

                <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32">
                    <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
                        <div className="relative">
                            <div className="sticky top-24">
                                <div className="text-8xl md:text-9xl font-black leading-none mb-8 text-stone-200">01</div>
                                <h2 className="text-5xl md:text-6xl font-black leading-tight mb-6">
                                    EVERY<br/>MONDAY &<br/>WEDNESDAY
                                </h2>
                                <div className="space-y-6">
                                    <div className="flex items-baseline gap-4">
                                        <Clock className="w-8 h-8 flex-shrink-0" />
                                        <div>
                                            <div className="text-6xl font-black">7:00 PM</div>
                                            <div className="text-2xl text-stone-500 font-bold">PACIFIC TIME</div>
                                        </div>
                                    </div>

                                    <div className="bg-black text-white p-8 mt-8">
                                        <div className="text-xs font-bold tracking-widest mb-4 text-red-400">WORLDWIDE TIMES</div>
                                        <div className="space-y-3 font-mono text-lg">
                                            <div className="flex justify-between">
                                                <span className="font-bold">EST</span>
                                                <span>06:00</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-bold">GMT</span>
                                                <span>11:00</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-bold">CET</span>
                                                <span>12:00</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-12">
                            <div>
                                <div className="text-8xl md:text-9xl font-black leading-none mb-8 text-stone-200">02</div>
                                <h2 className="text-5xl md:text-6xl font-black leading-tight mb-12">
                                    WHAT TO<br/>EXPECT
                                </h2>

                                <div className="space-y-8">
                                    {[
                                        { title: "SPIRITUAL GUIDANCE", icon: Star },
                                        { title: "PRAYER & MEDITATION", icon: Heart },
                                        { title: "DIVINE INSPIRATION", icon: Bell },
                                        { title: "COMMUNITY CONNECTION", icon: Users },
                                        { title: "TRANSFORMATION", icon: ArrowRight }
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-start gap-6 group cursor-pointer">
                                            <div className="bg-black text-red-400 p-4 flex-shrink-0 group-hover:bg-red-400 group-hover:text-black transition-colors">
                                                <item.icon className="w-8 h-8" />
                                            </div>
                                            <div className="pt-4">
                                                <h3 className="text-2xl font-black group-hover:translate-x-2 transition-transform">{item.title}</h3>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-black text-white py-20 md:py-32 relative">
                <img
                    src="https://www.thetransformationcollective.org/wp-content/uploads/2025/02/39191038.jpeg"
                    alt="Background"
                    className="absolute inset-0 w-full h-full object-cover opacity-5"
                />

                <div className="relative z-10 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="mb-16">
                        <div className="text-8xl md:text-9xl font-black leading-none mb-8 text-white/20">03</div>
                        <h2 className="text-5xl md:text-7xl font-black leading-tight mb-6">
                            JOIN OUR<br/>COMMUNITY
                        </h2>
                        <div className="w-32 h-1 bg-red-400"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                title: "PRAYER REQUESTS",
                                description: "Share your needs",
                                icon: Heart,
                                color: "red"
                            },
                            {
                                title: "TESTIMONIES",
                                description: "Share your experiences",
                                icon: Star,
                                color: "red"
                            },
                            {
                                title: "CONNECT GROUPS",
                                description: "Join small groups",
                                icon: Users,
                                color: "red"
                            }
                        ].map((item, index) => (
                            <div key={index} className="group cursor-pointer">
                                <div className="border-4 border-white p-8 h-full hover:bg-red-400 hover:border-red-400 transition-all">
                                    <item.icon className="w-16 h-16 mb-6 group-hover:scale-110 transition-transform" />
                                    <h3 className="text-2xl font-black mb-3 leading-tight">{item.title}</h3>
                                    <p className="text-lg mb-6 opacity-80">{item.description}</p>
                                    <div className="flex items-center gap-2 font-bold text-lg group-hover:translate-x-2 transition-transform">
                                        LEARN MORE
                                        <ArrowRight className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-red-400 py-20 md:py-32 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px]">
                    <Calendar className="w-full h-full opacity-10" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="max-w-3xl">
                        <h2 className="text-6xl md:text-8xl font-black leading-tight mb-8">
                            NEVER MISS<br/>A SESSION
                        </h2>
                        <p className="text-2xl md:text-3xl leading-relaxed mb-12 font-medium">
                            Sync Glory Hour with your calendar and receive notifications before each session starts.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <button className="px-10 py-5 bg-black text-white font-bold text-lg tracking-wide hover:bg-stone-900 transition-colors">
                                ADD TO GOOGLE CALENDAR
                            </button>
                            <button className="px-10 py-5 border-4 border-black text-black font-bold text-lg tracking-wide hover:bg-black hover:text-red-400 transition-all">
                                ADD TO APPLE CALENDAR
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* <footer className="bg-black text-white py-12 border-t-4 border-red-400">
                <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="text-center">
                        <div className="text-4xl font-black mb-4">GLORY HOUR</div>
                        <div className="text-red-400 font-bold tracking-wider">THE TRANSFORMATION COLLECTIVE</div>
                    </div>
                </div>
            </footer> */}
        </div>
    );
};

export default GloryHour;
